import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { getActivityLogs } from "store/slice/userActivityLogs/userActivitySlice";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  Card,
  Stack,
  Icon,
} from "@mui/material";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";

import MDDataGrid from "components/MDDataGrid/MDDataGrid";
import ActivityDetailDialog from "./components/ActivityDetailDialog";
import { dateToShow } from "global/functions";

const ActivityLogsPage = () => {
  const [selectedLog, setSelectedLog] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [targetType, setTargetType] = useState("");
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);

  const {
    data: logs = [],
    loading,
    meta,
  } = useSelector((state) => state.activityLogs);
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const dispatch = useDispatch();

  const TARGET_TYPES = [
    "Place",
    "Rate",
    "Permit",
    "Validation",
    "AutomatedValidation",
    "Message",
    "Report",
    "Payment",
    "Subscription",
  ];

  const fetchLogs = useCallback(() => {
    dispatch(
      getActivityLogs({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        searchTerm,
        targetType,
        filterModel: filterModel.items.length > 0 ? filterModel : undefined,
        sortModel: sortModel.length > 0 ? sortModel : undefined,
      }),
    );
  }, [
    dispatch,
    paginationModel,
    searchTerm,
    targetType,
    filterModel,
    sortModel,
  ]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const resetPage = () => setPaginationModel((prev) => ({ ...prev, page: 0 }));

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    resetPage();
  };

  const handleTargetTypeChange = (e) => {
    setTargetType(e.target.value);
    resetPage();
  };

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    resetPage();
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    resetPage();
  };

  const handleOpenDialog = (log) => {
    setSelectedLog(log);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedLog(null);
  };

  const columnsData = [
    // {
    //   field: "user",
    //   headerName: "User",
    //   width: 200,
    //   renderCell: (params) => (
    //     <Stack direction="row" spacing={1} alignItems="center">
    //       <Avatar sx={{ width: 32, height: 32 }}>
    //         {params.row.user.name?.charAt(0).toUpperCase()}
    //       </Avatar>
    //       <Box>
    //         <Typography variant="caption" color="text.secondary">
    //           {params.row.user.name || "Not Defined"}
    //         </Typography>
    //       </Box>
    //     </Stack>
    //   ),
    // },
    // {
    //   field: "email",
    //   headerName: "User Email",
    //   width: 150,
    //   renderCell: (params) => (
    //     <Box>
    //       <Typography variant="caption">{params.row.user.email}</Typography>
    //     </Box>
    //   ),
    // },
    {
      field: "action",
      headerName: "Action",
      width: 620,
      renderCell: (params) => (
        <Box>
          <Typography variant="body" sx={{ fontSize: "13px" }}>
            {params.row.action}
          </Typography>
        </Box>
      ),
    },
    {
      field: "target.type",
      headerName: "Target",
      width: 150,
      valueGetter: (value, row) => row?.target?.type ?? "",
      renderCell: (params) => (
        <Chip label={params.row.target.type} size="small" variant="outlined" />
        // <Box>
        //   <Typography variant="body2">{params.row.target.type}</Typography>
        // </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "Timestamp",
      width: 180,
      type: "date",  
      valueGetter: (value, row) => row?.createdAt ? new Date(row.createdAt) : null,
      renderCell: (params) => (
        <MDTypography variant="caption" color="text">
          {dateToShow(params.row.createdAt)}
        </MDTypography>
      ),
    },
    {
      field: "actionColumn",
      headerName: "Action",
      width: 100,
      sortable: false,
      filterable:false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" gap={2}>
          <Tooltip title="View Details" placement="top">
            <IconButton
              color="success"
              onClick={() => handleOpenDialog(params.row)}
            >
              <Icon fontSize="small">visibility</Icon>
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={1}
                mt={-2}
                py={1}
                px={1}
                variant="gradient"
                bgColor={sidenavColor}
                borderRadius="lg"
                coloredShadow={sidenavColor}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  User Activity
                </MDTypography>
              </MDBox>

              <Box sx={{ width: "100%", p: 3 }}>
                {/* Filters */}
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Search"
                        variant="outlined"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search by email, action or target..."
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Target Type</InputLabel>
                        <Select
                          label="Target Type"
                          value={targetType}
                          onChange={handleTargetTypeChange}
                        >
                          <MenuItem value="">All Types</MenuItem>
                          {TARGET_TYPES.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>

                {/* Table */}
                <MDDataGrid
                  rows={logs}
                  columns={columnsData}
                  getRowId={(row) => row._id}
                  pagination
                  paginationMode="server"
                  rowCount={meta?.totalRecords || 0}
                  paginationModel={paginationModel}
                  onPaginationModelChange={(newModel) =>
                    setPaginationModel(newModel)
                  }
                  pageSizeOptions={[10, 25, 50, 100]}
                  filterMode="server"
                  filterModel={filterModel}
                  onFilterModelChange={handleFilterModelChange}
                  sortingMode="server"
                  sortModel={sortModel}
                  onSortModelChange={handleSortModelChange}
                  disableSelectionOnClick
                  loading={loading}
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      {isDialogOpen && (
        <ActivityDetailDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          log={selectedLog}
        />
      )}
    </DashboardLayout>
  );
};

export default ActivityLogsPage;
